import { makePromise } from '../promise/makePromise';
import type { ReadonlyBuffer } from '../types';
import { isStr } from '../utils/common';

export interface FindIndexesOfOptions {
	allowOverlap?: boolean; // Defaults to false
	forceResume?: boolean; // Defaults to true
	forcePause?: boolean; // Defaults to false
}

class StreamSearcher_Text {
	#chunkStreamOffset = 0;
	matchIndexes: number[] = [];
	#potentialMatchIndexes: number[] = [];
	#needleLength: number;

	constructor(public needle: string, private allowOverlap: boolean) {
		this.#needleLength = needle.length;
	}

	pushChunk(chunk: string) {
		const nextPotentialMatchIndexes: number[] = [];

		let chunkSearchStartIndex = 0;

		// Do stuff with partial matches
		for (const potentialMatchIndex of this.#potentialMatchIndexes) {
			// length characters matched before this chunk
			const lengthMatchedLastChunk = this.#chunkStreamOffset - potentialMatchIndex;
			const lengthLeftToMatch = this.#needleLength - lengthMatchedLastChunk;

			if (lengthLeftToMatch <= chunk.length) {
				// We can determine if this match is a match
				if (chunk.startsWith(this.needle.slice(lengthMatchedLastChunk))) {
					this.matchIndexes.push(potentialMatchIndex);

					// If overlap is not allowed, then we can skip any remaining potential matches,
					// since they all will overlap the match we just found, and we have to change searchStart
					if (!this.allowOverlap) {
						chunkSearchStartIndex = lengthLeftToMatch;
						break;
					}
				}
			} else {
				// We can't determine if the whole string is here, but we can match some
				if (
					chunk ===
					this.needle.slice(lengthMatchedLastChunk, lengthMatchedLastChunk + chunk.length)
				) {
					// Potential match is still good
					nextPotentialMatchIndexes.push(potentialMatchIndex);
				}
			}
		}

		// Search through the chunk for whole matches
		while (true) {
			const index = chunk.indexOf(this.needle, chunkSearchStartIndex);
			if (index < 0) {
				break;
			}

			this.matchIndexes.push(index + this.#chunkStreamOffset);
			chunkSearchStartIndex = this.allowOverlap ? index + 1 : index + this.needle.length;
		}

		// Search for partial matches that trail off the end of the chunk
		for (
			let i = Math.min(this.#needleLength - 1, chunk.length - chunkSearchStartIndex);
			i > 0;
			--i
		) {
			if (chunk.endsWith(this.needle.slice(0, i))) {
				nextPotentialMatchIndexes.push(this.#chunkStreamOffset + chunk.length - i);
			}
		}

		this.#chunkStreamOffset += chunk.length;
		this.#potentialMatchIndexes = nextPotentialMatchIndexes;
	}
}

export async function findIndexesOf(
	stream: NodeJS.ReadableStream,
	needle: string,
	options?: FindIndexesOfOptions
): Promise<number[]> {
	if (!stream.readable) {
		throw new Error('Stream is not readable... has it already ended?');
	}

	const indexes = await makePromise<number[]>((resolve, reject) => {
		const searcher = new StreamSearcher_Text(needle, options?.allowOverlap ?? false);

		if (options?.forcePause) {
			stream.pause();
		}

		// eslint-disable-next-line @typescript-eslint/no-empty-function
		stream.on('data', (chunk: unknown) => {
			if (!isStr(chunk)) {
				throw new Error('findIndexesOf(): Expected stream to give string chunks');
			}

			if (!chunk.length) {
				return;
			}

			searcher.pushChunk(chunk);
		});

		stream.on('end', () => {
			resolve(searcher.matchIndexes);
		});

		stream.on('error', err => {
			reject(err);
		});

		// The stream may have already been explicitly paused, so we need to put in flowing mode
		if (options?.forceResume !== false) {
			stream.resume();
		}
	});

	return indexes;
}

export async function findIndexesOfMultiple(
	stream: NodeJS.ReadableStream,
	needles: string[],
	options?: FindIndexesOfOptions
): Promise<number[][]> {
	if (!stream.readable) {
		throw new Error('Stream is not readable... has it already ended?');
	}

	const indexes = await makePromise<number[][]>((resolve, reject) => {
		const searchers = needles.map(
			needle => new StreamSearcher_Text(needle, options?.allowOverlap ?? false)
		);

		if (options?.forcePause) {
			stream.pause();
		}

		// eslint-disable-next-line @typescript-eslint/no-empty-function
		stream.on('data', (chunk: unknown) => {
			if (!isStr(chunk)) {
				throw new Error('findIndexesOf(): Expected stream to give string chunks');
			}

			if (!chunk.length) {
				return;
			}

			for (const searcher of searchers) {
				searcher.pushChunk(chunk);
			}
		});

		stream.on('end', () => {
			resolve(searchers.map(o => o.matchIndexes));
		});

		stream.on('error', err => {
			reject(err);
		});

		// The stream may have already been explicitly paused, so we need to put in flowing mode
		if (options?.forceResume !== false) {
			stream.resume();
		}
	});

	return indexes;
}

export async function findIndexesOf_Buffer(
	stream: NodeJS.ReadableStream,
	needle: ReadonlyBuffer,
	options?: FindIndexesOfOptions
): Promise<number[]> {
	if (!stream.readable) {
		throw new Error('Stream is not readable... has it already ended?');
	}

	const nl = needle.length;

	const indexes = await makePromise<number[]>((resolve, reject) => {
		const _matchIndexes: number[] = [];
		let _potentialMatchIndexes: number[] = [];

		let chunkStreamOffset = 0;

		if (options?.forcePause) {
			stream.pause();
		}

		// eslint-disable-next-line @typescript-eslint/no-empty-function
		stream.on('data', (_chunk: unknown) => {
			if (!Buffer.isBuffer(_chunk)) {
				throw new Error('findIndexesOf_Buffer(): Expected stream to give Buffer chunks');
			}

			const chunk: ReadonlyBuffer = _chunk;
			if (!chunk.length) {
				return;
			}

			const nextPotentialMatchIndexes: number[] = [];

			let chunkSearchStartIndex = 0;

			// Do stuff with partial matches
			for (const potentialMatchIndex of _potentialMatchIndexes) {
				const lengthMatchedLastChunk = chunkStreamOffset - potentialMatchIndex;
				const lengthLeftToMatch = nl - lengthMatchedLastChunk;

				if (lengthLeftToMatch <= chunk.length) {
					// We can determine if this match is a match
					// Starts with
					if (
						chunk.compare(
							needle,
							lengthMatchedLastChunk,
							needle.length,
							0,
							lengthLeftToMatch
						) === 0
					) {
						_matchIndexes.push(potentialMatchIndex);

						// If overlap is not allowed, then we can skip any remaining potential matches,
						// since they all will overlap the match we just found, and we have to change searchStart
						if (!options?.allowOverlap) {
							chunkSearchStartIndex = lengthLeftToMatch;
							break;
						}
					}
				} else {
					// We can't determine if the whole string is here, but we can match some
					if (
						chunk.compare(
							needle,
							lengthMatchedLastChunk,
							lengthMatchedLastChunk + chunk.length,
							0,
							chunk.length
						) === 0
					) {
						// Potential match is still good
						nextPotentialMatchIndexes.push(potentialMatchIndex);
					}
				}
			}

			// Search through the chunk for whole matches
			while (true) {
				const index = chunk.indexOf(needle, chunkSearchStartIndex);
				if (index < 0) {
					break;
				}

				_matchIndexes.push(index + chunkStreamOffset);
				chunkSearchStartIndex = options?.allowOverlap ? index + 1 : index + needle.length;
			}

			// Search for partial matches that trail off the end of the chunk
			for (let i = Math.min(nl - 1, chunk.length - chunkSearchStartIndex); i > 0; --i) {
				// Ends with
				if (chunk.compare(needle, 0, i, chunk.length - i, chunk.length) === 0) {
					nextPotentialMatchIndexes.push(chunkStreamOffset + chunk.length - i);
				}
			}

			chunkStreamOffset += chunk.length;
			_potentialMatchIndexes = nextPotentialMatchIndexes;
		});

		stream.on('end', () => {
			resolve(_matchIndexes);
		});

		stream.on('error', err => {
			reject(err);
		});

		// The stream may have already been explicitly paused, so we need to put in flowing mode
		if (options?.forceResume !== false) {
			stream.resume();
		}
	});

	return indexes;
}
